import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifyDesktopFromPeerToast } from '../../utils/desktopNotifications';
import '../../css/common/PeerToastStack.css';

const MAX_VISIBLE = 3;
const MAX_TOTAL = 5; // 6件目は最古から捨てる
const AUTO_MS = 5000;
const RECENT_DEDUP_MS = 15000;
const MAX_RECENT_KEYS = 500;

const styleMap = {
  'offer.updated':   { iconKey: 'casting', color: 'var(--color-info)', label: 'オファー' },
  'contract.draft':  { iconKey: 'casting', color: 'var(--purple-600)', label: '本契約' },
  'contract.revision': { iconKey: 'casting', color: 'var(--purple-600)', label: '契約改訂' },
  'contract.responded': { iconKey: 'casting', color: 'var(--purple-600)', label: '本契約' },
  'invoice.updated': { iconKey: 'finance', color: 'var(--emerald-700)', label: '請求' },
  'supplemental_invoice.requested': { iconKey: 'finance', color: 'var(--emerald-700)', label: '追補請求' },
  'supplemental_invoice.updated': { iconKey: 'finance', color: 'var(--emerald-700)', label: '追補請求' },
  'supplemental_invoice.cancelled': { iconKey: 'finance', color: 'var(--gray-500)', label: '追補請求' },
  'supplemental_invoice.billed': { iconKey: 'finance', color: 'var(--emerald-700)', label: '追補請求' },
  'payment.scheduled': { iconKey: 'finance', color: 'var(--emerald-700)', label: '支払い予定' },
  'payment.paid':    { iconKey: 'finance', color: 'var(--green-700)', label: '支払い完了' },
  'chat.message':    { iconKey: 'chat', color: 'var(--color-success)', label: 'チャット' },
  'concierge.application': { iconKey: 'concierge', color: 'var(--purple-600)', label: 'コンシェルジュ' },
};

const iconClassMap = {
  chat: 'fa-solid fa-comment-dots',
  event: 'fa-solid fa-calendar-day',
  casting: 'fa-solid fa-handshake',
  finance: 'fa-solid fa-file-invoice-dollar',
  concierge: 'fa-solid fa-bell-concierge',
  default: 'fa-solid fa-bell',
};

const iconKeyAliases = {
  chat: 'chat',
  event: 'event',
  offer: 'casting',
  contract: 'casting',
  invoice: 'finance',
  payment: 'finance',
  concierge: 'concierge',
};

const resolveIconClass = (rawIcon, type) => {
  if (rawIcon && typeof rawIcon === 'string' && rawIcon.includes('fa-')) {
    return rawIcon;
  }
  const baseKey = styleMap[type]?.iconKey;
  const normalizedKey = iconKeyAliases[rawIcon] || rawIcon || baseKey || 'default';
  return iconClassMap[normalizedKey] || iconClassMap.default;
};

const buildAggregateKey = (raw = {}, normalized = {}) => {
  if (raw.aggregateKey) return String(raw.aggregateKey);
  return [
    normalized.audience || raw.audience || 'all',
    normalized.type || raw.type || 'info',
    normalized.title || raw.title || '',
    normalized.targetUrl || raw.targetUrl || '',
  ].join(':');
};

const buildDedupeKey = (raw = {}) => {
  if (raw.dedupeKey) return String(raw.dedupeKey);
  if (raw.id) return String(raw.id);
  return '';
};

const pruneRecentKeys = (recentKeys) => {
  if (!(recentKeys instanceof Map) || recentKeys.size <= MAX_RECENT_KEYS) return;
  const entries = Array.from(recentKeys.entries()).sort((a, b) => a[1] - b[1]);
  for (let i = 0; i < entries.length - MAX_RECENT_KEYS; i += 1) {
    recentKeys.delete(entries[i][0]);
  }
};

const buildAggregateSummary = (toast, count) => {
  if (count <= 1) return toast.summary || '';
  const label = toast.aggregateLabel || toast.title || '通知';
  return `${label}が${count}件あります`;
};

function normalizeToast(raw) {
  const base = styleMap[raw.type] || {};
  const iconKey = raw.iconKey || raw.icon || base.iconKey || 'default';
  const normalized = {
    id: raw.id || `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    type: raw.type || 'info',
    title: raw.title || base.label || 'お知らせ',
    summary: raw.summary || '',
    targetUrl: raw.targetUrl || '',
    audience: raw.audience || 'all',
    iconKey,
    iconClass: resolveIconClass(iconKey, raw.type),
    color: raw.color || base.color || 'var(--color-text-strong)',
    aggregateLabel: raw.aggregateLabel || raw.title || base.label || '通知',
    aggregateCount: Number.isFinite(Number(raw.aggregateCount)) ? Math.max(1, Number(raw.aggregateCount)) : 1,
    createdAt: Date.now(),
  };
  normalized.aggregateKey = buildAggregateKey(raw, normalized);
  normalized.dedupeKey = buildDedupeKey(raw);
  return normalized;
}

const PeerToastStack = () => {
  const [active, setActive] = useState([]);
  const [queue, setQueue] = useState([]);
  const queueRef = useRef([]);
  const timersRef = useRef(new Map());
  const recentDedupeKeysRef = useRef(new Map());
  const [isVisible, setIsVisible] = useState(
    typeof document === 'undefined' ? true : document.visibilityState !== 'hidden'
  );
  const navigate = useNavigate();

  const combined = useMemo(() => [...active, ...queue], [active, queue]);

  const remove = useCallback((id) => {
    setActive((prev) => prev.filter((t) => t.id !== id));
    setQueue((prev) => {
      const next = prev.filter((t) => t.id !== id);
      queueRef.current = next;
      return next;
    });
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current.clear();
  }, []);

  const shiftIfNeeded = useCallback(() => {
    setActive((prevActive) => {
      if (prevActive.length >= MAX_VISIBLE || queueRef.current.length === 0) return prevActive;
      const [next, ...rest] = queueRef.current;
      queueRef.current = rest;
      setQueue(rest);
      return [...prevActive, next];
    });
  }, []);

  const push = useCallback((rawToast) => {
    const toast = normalizeToast(rawToast || {});
    const now = Date.now();
    if (toast.dedupeKey) {
      const lastTs = recentDedupeKeysRef.current.get(toast.dedupeKey);
      if (lastTs && now - lastTs < RECENT_DEDUP_MS) {
        return;
      }
      recentDedupeKeysRef.current.set(toast.dedupeKey, now);
      pruneRecentKeys(recentDedupeKeysRef.current);
    }
    setActive((prevActive) => {
      const current = [...prevActive, ...queueRef.current];
      const existingIndex = current.findIndex((item) => item.aggregateKey === toast.aggregateKey);
      if (existingIndex >= 0) {
        const existing = current[existingIndex];
        const nextCount = (existing.aggregateCount || 1) + 1;
        current[existingIndex] = {
          ...existing,
          ...toast,
          id: existing.id,
          aggregateCount: nextCount,
          summary: buildAggregateSummary(toast, nextCount),
          createdAt: existing.createdAt || toast.createdAt,
          updatedAt: now,
        };
      } else {
        current.push(toast);
      }
      while (current.length > MAX_TOTAL) current.shift();
      const nextActive = current.slice(0, MAX_VISIBLE);
      const nextQueue = current.slice(MAX_VISIBLE);
      queueRef.current = nextQueue;
      setQueue(nextQueue);
      return nextActive;
    });
    void notifyDesktopFromPeerToast(toast);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (!event?.detail) return;
      push(event.detail);
    };
    window.addEventListener('peer:notification', handler);
    return () => {
      window.removeEventListener('peer:notification', handler);
      clearAllTimers();
    };
  }, [push, clearAllTimers]);

  useEffect(() => {
    clearAllTimers();
    if (!isVisible) return;
    active.forEach((toast) => {
      const timer = setTimeout(() => remove(toast.id), AUTO_MS);
      timersRef.current.set(toast.id, timer);
    });
  }, [active, remove, isVisible, clearAllTimers]);

  useEffect(() => {
    if (active.length < MAX_VISIBLE && queue.length > 0) {
      shiftIfNeeded();
    }
  }, [active, queue, shiftIfNeeded]);

  useEffect(() => {
    const onVis = () => setIsVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  if (!combined.length) return null;

  return (
    <div className="peer-toast-stack" aria-live="polite" aria-atomic="true">
      {active.map((toast) => (
        <button
          key={toast.id}
          type="button"
          className="peer-toast"
          onClick={() => {
            if (toast.targetUrl) {
              navigate(toast.targetUrl);
            }
            remove(toast.id);
          }}
        >
          <span
            className="peer-toast__icon"
            aria-hidden="true"
            style={{ backgroundColor: `color-mix(in srgb, ${toast.color} 13%, transparent)`, color: toast.color }}
          >
            <i className={toast.iconClass} aria-hidden="true" />
          </span>
          <span className="peer-toast__body">
            <span className="peer-toast__title">{toast.title}</span>
            <span className="peer-toast__summary">{toast.summary}</span>
          </span>
          <span
            className="peer-toast__close"
            onClick={(e) => {
              e.stopPropagation();
              remove(toast.id);
            }}
            aria-label="閉じる"
          >
            ×
          </span>
        </button>
      ))}
    </div>
  );
};

export default PeerToastStack;
